import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "flowbite-react";
import { Product, Parameter } from "../../models";
import { getProductTypeAsArray } from "../../utils/productHelpers";
import { CatalogCategoryGrid } from "../Catalog/CatalogCategoryGrid";
import { emptyProductIllustration } from "../Icons/illustrations";
import { cn, panelInteractive } from "@design-system";

interface ProductCategoriesProps {
  products: Product[];
  categories: Parameter[];
  isLoading: boolean;
  onSelectCategory: (categoryId: string | null) => void;
  onSelectProduct: (product: Product) => void;
  selectedCategoryId: string | null;
  onAddCategory?: () => void;
  onDeleteCategory?: (categoryId: string) => void;
}

// Separate component for product card to improve readability and reusability
interface ProductCardProps {
  product: Product;
  categories: Parameter[];
  onClick: () => void;
  t: any;
}

const ProductCard: FC<ProductCardProps> = ({
  product,
  categories,
  onClick,
  t,
}) => {
  // Memoize category badges to avoid unnecessary re-renders
  const categoryBadges = useMemo(() => {
    const productTypes = getProductTypeAsArray(product);

    if (productTypes.length === 0) {
      return (
        <Badge key="uncategorized" color="gray" className="text-xs bg-surface-muted text-secondary">
          {t("products:categories.uncategorized")}
        </Badge>
      );
    }

    return productTypes.map((typeId) => {
      const category = categories.find((c) => c.id === typeId);
      return category ? (
        <Badge key={typeId} color="purple" className="text-xs bg-pickup-muted text-pickup">
          {category.code}
        </Badge>
      ) : null;
    });
  }, [product, categories, t]);

  const imageUrl = product.imgUrls?.[0];

  return (
    <div
      className={cn(panelInteractive, "cursor-pointer p-3 flex items-center gap-3")}
      onClick={onClick}
    >
      {/* Image - only show if image exists */}
      {imageUrl && (
        <div className="relative h-20 w-20 flex-shrink-0">
          <img
            src={imageUrl}
            alt={product.descriptionShort}
            className="h-full w-full object-cover rounded-md"
          />
        </div>
      )}

      {/* Details */}
      <div className="flex-1 min-w-0">
        {/* Title */}
        <div className="flex justify-between items-start gap-2">
          <h5 className="text-base font-semibold tracking-tight text-text truncate">
            {product.descriptionShort}
          </h5>
        </div>

        {/* Middle row: Description */}
        <p className="text-sm text-muted mt-1 truncate">
          {product.description || t("products:no_description")}
        </p>

        {/* Bottom row: Badges */}
        <div className="flex flex-wrap gap-2 mt-2">{categoryBadges}</div>
      </div>
    </div>
  );
};

export const ProductCategories: FC<ProductCategoriesProps> = ({
  products,
  categories,
  isLoading,
  onSelectCategory,
  onSelectProduct,
  selectedCategoryId,
  onAddCategory,
  onDeleteCategory,
}) => {
  const { t } = useTranslation(["common", "products"]);

  return (
    <CatalogCategoryGrid<Product>
      items={products}
      categories={categories}
      isLoading={isLoading}
      selectedCategoryId={selectedCategoryId}
      onSelectCategory={onSelectCategory}
      onSelectItem={onSelectProduct}
      onAddCategory={onAddCategory}
      onDeleteCategory={onDeleteCategory}
      t={t}
      namespace="products"
      labels={{
        allLabel: t("products:categories.all_products"),
        noItems: {
          title: t("products:no_products.title"),
          description: t("products:no_products.description"),
          buttonText: t("products:no_products.button_text"),
        },
      }}
      searchInputId="productSearch"
      emptyIllustration={emptyProductIllustration}
      getCategoryIdForItem={(product, cats) => {
        const productTypeArray = getProductTypeAsArray(product);
        if (productTypeArray.length === 0) return null;
        const categoryId = productTypeArray[0];
        return cats.some((c) => c.id === categoryId) ? categoryId : null;
      }}
      matchesSearch={(product, searchLower) =>
        product.descriptionShort.toLowerCase().includes(searchLower) ||
        (!!product.description &&
          product.description.toLowerCase().includes(searchLower))
      }
      renderCard={(product) => (
        <ProductCard
          key={product.id}
          product={product}
          categories={categories}
          onClick={() => onSelectProduct(product)}
          t={t}
        />
      )}
      createDefaultItem={(selId) => {
        const newProduct = Product.default();
        if (selId && selId !== "uncategorized") {
          newProduct.productType = [selId];
        }
        return newProduct;
      }}
      alwaysShowSearch={true}
      showNoSearchResults={true}
    />
  );
};
